import { createSignal, createEffect, onCleanup, For, Show, createMemo, onMount } from "solid-js";
import { customElement } from "solid-element";
import { startAiTemplateGeneration, getAiTemplateGenerationStatus } from "../../services/cheetah-api";
import { checkLoginStatus } from "../../services/office";
import {setTranslations, translations} from "../../services/context";
import { getTranslations } from "../../services/translation";

document.addEventListener("DOMContentLoaded", function(event) {
    getTranslations(typeof configBaTemplatesList != 'undefined' ? (configBaTemplatesList.lang || 'en') : 'en').then(r => setTranslations(r))
});
type WebsiteForm = {
    name: string,
    description: string,
    focus: string
}

enum PromptQuality {
    Nice = 80,
    Perfect = 160
}

customElement(
    "builderall-ai-wizard",
    { show: false, templateid: null, description: ''},
    (props) => {
        console.log(props.description)
        const [form, setForm] = createSignal<WebsiteForm>({description: props.description, name: '', focus: ''});
        const [errors, setErrors] = createSignal<WebsiteForm>({name: "", description: "", focus: ""});
        const [loading, setLoading] = createSignal(false);
        const [generation, setGeneration] = createSignal({
            id: null,
            status: "PENDING",
            interval: null,
        });

        const promptStatusClass = createMemo(() => {
            return `prompt-status-box ${form().description ? (
                form().description.length < PromptQuality.Nice ? 'red' :
                form().description.length < PromptQuality.Perfect ? 'yellow' : 'green') : 
            ''}`;
        });

        const promptStatusText = createMemo(() => {
            return form().description
                ? form().description.length < PromptQuality.Nice
                ? translations['ct-prompt-health-red']
                : form().description.length < PromptQuality.Perfect
                    ? translations['ct-prompt-health-yellow']
                    : translations['ct-prompt-health-green']
                : '';
        });

        const websiteFocusOptions = [
            { value: 'product',        label: translations['ct-ai-product'] },
            { value: 'service',        label: translations['ct-ai-service'] },
            { value: 'courses',        label: translations['ct-ai-courses'] },
            { value: 'lead-capture',   label: translations['ct-ai-lead-capture'] },
            { value: 'personal-brand', label: translations['ct-ai-personal-brand'] },
            { value: 'membership',     label: translations['ct-ai-membership'] },
            { value: 'promotions',     label: translations['ct-ai-promotions'] },
            { value: 'agency',         label: translations['ct-ai-agency'] },
            { value: 'blog-media',     label: translations['ct-ai-blog-media'] }
        ];

        function isValid() {
            const errs: WebsiteForm = {name: "", description: "", focus: ""};
            if (!form().name) {
                errs.name = translations['ct-ai-validation-brand-website-name'];
            } else if (form().name.length > 255) {
                errs.name = translations['ct-ai-validation-name-characters'];
            }

            if (!form().focus) errs.focus = translations['ct-ai-validation-focus'];

            if (!form().description) {
                errs.description = translations['ct-ai-validation-description'];
            } else if (form().description.length > 500) {
                errs.description = translations['ct-ai-validation-description-characters'];
            }

            setErrors(errs);
            return Object.values(errs).every(e => !e)
        }

        async function createWebsite() {
            if (!isValid()) return;

            setLoading(true);
            const officeLogin = await checkLoginStatus();
            if (!officeLogin || !officeLogin.isLogged || !officeLogin.email) {
                alert('Not authorized. Please log in to the Builderall platform and try again.')
                setLoading(false);
                return;
            }

            const formData = {
                name: form().name,
                description: form().description,
                focus: form().focus,
                template_id: props.templateid,
                user_email: officeLogin.email,
                language: getLangByLocationHost()
            };

            try {
                const response = await startAiTemplateGeneration(formData);
                console.log(response)

                if (response.success) {
                    setGeneration((g) => ({
                        ...g,
                        id: response.id,
                        status: "PENDING",
                        interval: setInterval(() => {
                            checkGenerationStatus()
                        }, 5000),
                    }));
                }
            } catch (e) {
                console.error(e);
                setLoading(false);
            }
        }

        async function checkGenerationStatus() {

            try {
                const response = await getAiTemplateGenerationStatus(generation().id);

                console.log(response);
                const status = response.status;

                setGeneration((g) => ({ ...g, status }));

                if (status === "SUCCESS") {
                    clearInterval(generation().interval);
                    // setLoading(false);
                    console.log("Redirect to website", response.website);
                    location.href = response.website
                } else if (status === "ERROR") {
                    clearInterval(generation().interval);
                    setLoading(false);
                    alert("Error generating website");
                }
            } catch (e) {
                console.error(e);
            }
        }

        function destroyOnClickOutside(e) {
            if (e.target.classList.contains("ai-wizard-modal") && !loading()) {
                document.querySelector("builderall-ai-wizard").remove()
            }
        }

        function getLangByLocationHost() {
            const langMapping = {
                'builderall.com': 'en_US',
                'es.builderall.com': 'es_ES',
                'it.builderall.com': 'it_IT',
                'br.builderall.com': 'pt_BR',
                'builderall.de': 'de_DE',
                'fr.builderall.com': 'fr_FR'
            }
            const languageFromParam = new URLSearchParams(location.search).get('language') 
            return Object.values(langMapping).includes(languageFromParam) 
                ? languageFromParam 
                : langMapping[location.host] 
                || langMapping['builderall.com']
        }

        onCleanup(() => {
            if (generation().interval) clearInterval(generation().interval);
        });

        return (
            <>
                <Show when={props.show}>

                <style>{require("bundle-text:./styles/index.scss")}</style>

                <div class="ai-wizard-modal modal-backdrop" onClick={destroyOnClickOutside}>
                    {/*@ts-ignore*/}

                    <builderall-loader 
                        loading={loading()}
                        class="modal"
                        text={`${translations['ct-ai-loading-message-1']}|${translations['ct-ai-loading-message-2']}|
                            ${translations['ct-ai-loading-message-3']}|${translations['ct-ai-loading-message-4']}|
                            ${translations['ct-ai-loading-message-5']}|${translations['ct-ai-loading-message-6']}`}
                        carousel="true"
                        >
                        <div class="popup-header">
                            <svg width="27" height="26" viewBox="0 0 27 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.87884 3.49719C7.5226 1.47922 10.7529 0.664149 13.8281 1.00793C13.9901 1.02609 14.1472 1.07665 14.29 1.1554C14.4327 1.23418 14.5583 1.34051 14.6601 1.4679C14.7619 1.59537 14.8384 1.74182 14.8837 1.89856C14.9289 2.05509 14.9428 2.21907 14.9247 2.38098C14.9066 2.54311 14.8561 2.70004 14.7773 2.8429C14.6985 2.98577 14.5923 3.11215 14.4648 3.21399C14.3375 3.31561 14.1916 3.39137 14.0351 3.43665C13.8784 3.48193 13.7138 3.49578 13.5517 3.47766C11.0977 3.20331 8.51055 3.85076 6.38665 5.4718C2.01192 8.81103 1.17731 15.0277 4.51653 19.4025C7.85571 23.7768 14.0716 24.6114 18.4462 21.2726C20.5934 19.6337 21.8896 17.3453 22.2617 14.8927C22.2818 14.728 22.3343 14.5688 22.4169 14.4249C22.4997 14.2809 22.6108 14.1543 22.7431 14.0538C22.8753 13.9535 23.0266 13.881 23.1874 13.84C23.3484 13.7989 23.5163 13.7907 23.6806 13.8156C23.8449 13.8404 24.0028 13.8976 24.1445 13.9845C24.286 14.0714 24.4082 14.1863 24.5048 14.3214C24.6014 14.4565 24.6706 14.6095 24.707 14.7716C24.7433 14.9336 24.7463 15.1014 24.7167 15.2648C24.25 18.341 22.6081 21.2213 19.954 23.2472C14.5119 27.4011 6.69492 26.3514 2.54095 20.9093C-1.61266 15.4672 -0.563111 7.65108 4.87884 3.49719ZM12.707 11.0597C12.8785 11.0598 13.0458 11.1118 13.1874 11.2081C13.3292 11.3046 13.4385 11.4414 13.5009 11.6007L13.7783 12.3019C13.8208 12.4106 13.8852 12.51 13.9677 12.5929C14.0503 12.6758 14.1498 12.7411 14.2587 12.7843L14.9609 13.0597C15.1202 13.1229 15.2571 13.2328 15.3534 13.3741C15.4498 13.5157 15.5019 13.6836 15.5019 13.8546C15.5018 14.0255 15.4497 14.1927 15.3534 14.3341C15.2571 14.4754 15.1202 14.5853 14.9609 14.6486L14.2587 14.9259C14.15 14.9689 14.0513 15.0338 13.9687 15.1163C13.8861 15.1989 13.821 15.2979 13.7783 15.4064L13.5009 16.1095C13.4385 16.2688 13.3292 16.4056 13.1874 16.5021C13.0458 16.5984 12.8785 16.6504 12.707 16.6505C12.5353 16.6505 12.3673 16.5985 12.2255 16.5021C12.0838 16.4056 11.9744 16.2688 11.912 16.1095L11.6347 15.4064C11.592 15.298 11.5277 15.1989 11.4452 15.1163C11.3626 15.0338 11.263 14.9689 11.1542 14.9259L10.4521 14.6486C10.2927 14.5853 10.1559 14.4755 10.0595 14.3341C9.96323 14.1927 9.91116 14.0255 9.91106 13.8546C9.91106 13.6836 9.96314 13.5157 10.0595 13.3741C10.1559 13.2328 10.2927 13.1229 10.4521 13.0597L11.1542 12.7843C11.2632 12.7417 11.3626 12.6765 11.4452 12.5939C11.5278 12.5113 11.5923 12.4125 11.6347 12.3038L11.912 11.6007C11.9744 11.4414 12.0838 11.3046 12.2255 11.2081C12.3673 11.1116 12.5353 11.0597 12.707 11.0597ZM19.2284 0.500122C19.489 0.500122 19.7438 0.578365 19.9589 0.724731C20.1741 0.871239 20.3398 1.07949 20.4345 1.32141L21.5878 4.24231C21.6529 4.40762 21.7511 4.55792 21.8769 4.68372C22.0028 4.80958 22.1534 4.90834 22.3193 4.97375L25.2411 6.12708C25.4832 6.22317 25.6906 6.38977 25.8368 6.60461C25.9831 6.81953 26.0615 7.07347 26.0615 7.33313C26.0614 7.5926 25.983 7.84588 25.8368 8.06067C25.6906 8.27549 25.4832 8.4421 25.2411 8.53821L22.3193 9.69153C22.1538 9.7566 22.0037 9.85533 21.8779 9.98059C21.752 10.106 21.6532 10.2561 21.5878 10.421L20.4345 13.3448C20.3398 13.5866 20.174 13.7941 19.9589 13.9406C19.7437 14.087 19.4891 14.1652 19.2284 14.1652C18.9679 14.1651 18.7131 14.087 18.498 13.9406C18.283 13.7941 18.1171 13.5866 18.0224 13.3448L16.87 10.423C16.8047 10.2582 16.7057 10.1088 16.58 9.98352C16.4542 9.85821 16.3041 9.75958 16.1386 9.69446L13.2167 8.54114C12.9746 8.44505 12.7663 8.27851 12.62 8.0636C12.4739 7.84876 12.3964 7.59461 12.3964 7.33508C12.3965 7.07574 12.474 6.82225 12.62 6.60754C12.7663 6.39263 12.9746 6.22609 13.2167 6.13L16.1406 4.97668C16.3061 4.9114 16.4565 4.81249 16.582 4.68665C16.7074 4.56081 16.8057 4.41056 16.87 4.24524L18.0224 1.32141C18.1171 1.07952 18.2828 0.871237 18.498 0.724731C18.7131 0.578277 18.9679 0.500179 19.2284 0.500122ZM23.8876 1.74231C23.9448 1.74235 24.0005 1.75998 24.0478 1.79211C24.0951 1.82429 24.1315 1.86986 24.1523 1.92297V1.922L24.245 2.15637C24.2593 2.19252 24.281 2.22553 24.3085 2.25305C24.3361 2.28057 24.3689 2.30216 24.4052 2.31653L24.6396 2.4093C24.6926 2.43048 24.7384 2.46674 24.7704 2.51379C24.8024 2.56092 24.8193 2.61706 24.8193 2.67395C24.8193 2.73064 24.8022 2.78611 24.7704 2.83313C24.7384 2.88026 24.6926 2.9174 24.6396 2.9386L24.4052 3.03137C24.369 3.04574 24.3361 3.06734 24.3085 3.09485C24.281 3.12236 24.2593 3.15541 24.245 3.19153L24.1523 3.4259C24.1314 3.47888 24.095 3.52466 24.0478 3.55676C24.0006 3.5888 23.9447 3.60555 23.8876 3.60559C23.8303 3.60559 23.7738 3.58894 23.7265 3.55676C23.6793 3.52462 23.6428 3.4789 23.622 3.4259L23.5302 3.19153C23.5159 3.1554 23.4943 3.12234 23.4667 3.09485C23.4392 3.06742 23.4063 3.04567 23.37 3.03137L23.1357 2.9386C23.0826 2.9174 23.0368 2.88026 23.0048 2.83313C22.9729 2.78607 22.956 2.73072 22.956 2.67395C22.956 2.61706 22.9728 2.56092 23.0048 2.51379C23.0368 2.46683 23.0828 2.43045 23.1357 2.4093L23.37 2.31653C23.4435 2.28774 23.5008 2.22954 23.5302 2.15637L23.622 1.92297C23.6427 1.87 23.6794 1.82427 23.7265 1.79211C23.7738 1.75994 23.8303 1.74231 23.8876 1.74231Z" fill="url(#paint0_linear_223_1776)"/>
                            <defs>
                                <linearGradient id="paint0_linear_223_1776" x1="19.2288" y1="0.500122" x2="19.2288" y2="14.1656" gradientUnits="userSpaceOnUse">
                                <stop stop-color="#0072E1"/>
                                <stop offset="1" stop-color="#003E7B"/>
                                </linearGradient>
                            </defs>
                            </svg>
                            <span>{translations['ct-ai-website-builder']}</span>
                        </div>

                        <div class="popup-body">
                            <p>{translations['ct-ai-website-builder-description']}</p>

                            {/* Website Name */}
                            <div class="box-input">
                                <label for="website-name">{translations['ct-ai-brand-website-name']}</label>
                                <input
                                    id="website-name"
                                    classList={{"ba-input ba-md": true, "ba-error": !!errors().name }}
                                    value={form().name}
                                    placeholder={translations['ct-ai-brand-website-name-placeholder']}
                                    onInput={(e) => {
                                        setForm({ ...form(), name: e.currentTarget.value })
                                        setErrors({ ...errors(), name: ''})
                                    }}
                                />
                                <Show when={!!errors().name}>
                                    <span class="error-message">{errors().name}</span>
                                </Show>
                            </div>

                            {/* Description */}
                            <div class="box-input">
                                <label for="website-description">{translations['ct-description']}</label>
                                <textarea
                                    id="website-description"
                                    classList={{
                                        "ba-input ba-md": true,
                                        "ba-error": !!errors().description,
                                    }}
                                    value={form().description}
                                    placeholder={translations['ct-ai-description-placeholder']}
                                    onInput={(e) => {
                                        setForm({ ...form(), description: e.currentTarget.value })
                                        setErrors({ ...errors(), description: ''})
                                    }}
                                />
                                <Show when={!!errors().description}>
                                    <span class="error-message">{errors().description}</span>
                                </Show>
                                <div class={promptStatusClass()}>
                                    <div class="prompt-status-1" />
                                    <div class="prompt-status-2" />
                                    <div class="prompt-status-3" />
                                </div>
                                <span class="instructions"> {promptStatusText()}</span>
                            </div>

                            {/* Focus */}
                            <div class="box-radio">
                                <label>{translations['ct-ai-business-focus']}</label>
                                <Show when={errors().focus && !form().focus}>
                                    <span class="error-message">{errors().focus}</span>
                                </Show>

                                <div class="radio-grid">
                                    <For each={websiteFocusOptions}>
                                        {(option) => (
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="website-focus"
                                                    value={option.value}
                                                    checked={form().focus === option.value}
                                                    onChange={(e) =>
                                                        setForm({ ...form(), focus: e.currentTarget.value })
                                                    }
                                                />
                                                {option.label}
                                            </label>
                                        )}
                                    </For>
                                </div>
                            </div>
                        </div>

                        <div class="popup-bottom">
                            <button class="generate-button" onClick={createWebsite} disabled={loading()}>
                                <svg width="22" height="22" viewBox="0 0 27 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.87884 3.49719C7.5226 1.47922 10.7529 0.664149 13.8281 1.00793C13.9901 1.02609 14.1472 1.07665 14.29 1.1554C14.4327 1.23418 14.5583 1.34051 14.6601 1.4679C14.7619 1.59537 14.8384 1.74182 14.8837 1.89856C14.9289 2.05509 14.9428 2.21907 14.9247 2.38098C14.9066 2.54311 14.8561 2.70004 14.7773 2.8429C14.6985 2.98577 14.5923 3.11215 14.4648 3.21399C14.3375 3.31561 14.1916 3.39137 14.0351 3.43665C13.8784 3.48193 13.7138 3.49578 13.5517 3.47766C11.0977 3.20331 8.51055 3.85076 6.38665 5.4718C2.01192 8.81103 1.17731 15.0277 4.51653 19.4025C7.85571 23.7768 14.0716 24.6114 18.4462 21.2726C20.5934 19.6337 21.8896 17.3453 22.2617 14.8927C22.2818 14.728 22.3343 14.5688 22.4169 14.4249C22.4997 14.2809 22.6108 14.1543 22.7431 14.0538C22.8753 13.9535 23.0266 13.881 23.1874 13.84C23.3484 13.7989 23.5163 13.7907 23.6806 13.8156C23.8449 13.8404 24.0028 13.8976 24.1445 13.9845C24.286 14.0714 24.4082 14.1863 24.5048 14.3214C24.6014 14.4565 24.6706 14.6095 24.707 14.7716C24.7433 14.9336 24.7463 15.1014 24.7167 15.2648C24.25 18.341 22.6081 21.2213 19.954 23.2472C14.5119 27.4011 6.69492 26.3514 2.54095 20.9093C-1.61266 15.4672 -0.563111 7.65108 4.87884 3.49719ZM12.707 11.0597C12.8785 11.0598 13.0458 11.1118 13.1874 11.2081C13.3292 11.3046 13.4385 11.4414 13.5009 11.6007L13.7783 12.3019C13.8208 12.4106 13.8852 12.51 13.9677 12.5929C14.0503 12.6758 14.1498 12.7411 14.2587 12.7843L14.9609 13.0597C15.1202 13.1229 15.2571 13.2328 15.3534 13.3741C15.4498 13.5157 15.5019 13.6836 15.5019 13.8546C15.5018 14.0255 15.4497 14.1927 15.3534 14.3341C15.2571 14.4754 15.1202 14.5853 14.9609 14.6486L14.2587 14.9259C14.15 14.9689 14.0513 15.0338 13.9687 15.1163C13.8861 15.1989 13.821 15.2979 13.7783 15.4064L13.5009 16.1095C13.4385 16.2688 13.3292 16.4056 13.1874 16.5021C13.0458 16.5984 12.8785 16.6504 12.707 16.6505C12.5353 16.6505 12.3673 16.5985 12.2255 16.5021C12.0838 16.4056 11.9744 16.2688 11.912 16.1095L11.6347 15.4064C11.592 15.298 11.5277 15.1989 11.4452 15.1163C11.3626 15.0338 11.263 14.9689 11.1542 14.9259L10.4521 14.6486C10.2927 14.5853 10.1559 14.4755 10.0595 14.3341C9.96323 14.1927 9.91116 14.0255 9.91106 13.8546C9.91106 13.6836 9.96314 13.5157 10.0595 13.3741C10.1559 13.2328 10.2927 13.1229 10.4521 13.0597L11.1542 12.7843C11.2632 12.7417 11.3626 12.6765 11.4452 12.5939C11.5278 12.5113 11.5923 12.4125 11.6347 12.3038L11.912 11.6007C11.9744 11.4414 12.0838 11.3046 12.2255 11.2081C12.3673 11.1116 12.5353 11.0597 12.707 11.0597ZM19.2284 0.500122C19.489 0.500122 19.7438 0.578365 19.9589 0.724731C20.1741 0.871239 20.3398 1.07949 20.4345 1.32141L21.5878 4.24231C21.6529 4.40762 21.7511 4.55792 21.8769 4.68372C22.0028 4.80958 22.1534 4.90834 22.3193 4.97375L25.2411 6.12708C25.4832 6.22317 25.6906 6.38977 25.8368 6.60461C25.9831 6.81953 26.0615 7.07347 26.0615 7.33313C26.0614 7.5926 25.983 7.84588 25.8368 8.06067C25.6906 8.27549 25.4832 8.4421 25.2411 8.53821L22.3193 9.69153C22.1538 9.7566 22.0037 9.85533 21.8779 9.98059C21.752 10.106 21.6532 10.2561 21.5878 10.421L20.4345 13.3448C20.3398 13.5866 20.174 13.7941 19.9589 13.9406C19.7437 14.087 19.4891 14.1652 19.2284 14.1652C18.9679 14.1651 18.7131 14.087 18.498 13.9406C18.283 13.7941 18.1171 13.5866 18.0224 13.3448L16.87 10.423C16.8047 10.2582 16.7057 10.1088 16.58 9.98352C16.4542 9.85821 16.3041 9.75958 16.1386 9.69446L13.2167 8.54114C12.9746 8.44505 12.7663 8.27851 12.62 8.0636C12.4739 7.84876 12.3964 7.59461 12.3964 7.33508C12.3965 7.07574 12.474 6.82225 12.62 6.60754C12.7663 6.39263 12.9746 6.22609 13.2167 6.13L16.1406 4.97668C16.3061 4.9114 16.4565 4.81249 16.582 4.68665C16.7074 4.56081 16.8057 4.41056 16.87 4.24524L18.0224 1.32141C18.1171 1.07952 18.2828 0.871237 18.498 0.724731C18.7131 0.578277 18.9679 0.500179 19.2284 0.500122ZM23.8876 1.74231C23.9448 1.74235 24.0005 1.75998 24.0478 1.79211C24.0951 1.82429 24.1315 1.86986 24.1523 1.92297V1.922L24.245 2.15637C24.2593 2.19252 24.281 2.22553 24.3085 2.25305C24.3361 2.28057 24.3689 2.30216 24.4052 2.31653L24.6396 2.4093C24.6926 2.43048 24.7384 2.46674 24.7704 2.51379C24.8024 2.56092 24.8193 2.61706 24.8193 2.67395C24.8193 2.73064 24.8022 2.78611 24.7704 2.83313C24.7384 2.88026 24.6926 2.9174 24.6396 2.9386L24.4052 3.03137C24.369 3.04574 24.3361 3.06734 24.3085 3.09485C24.281 3.12236 24.2593 3.15541 24.245 3.19153L24.1523 3.4259C24.1314 3.47888 24.095 3.52466 24.0478 3.55676C24.0006 3.5888 23.9447 3.60555 23.8876 3.60559C23.8303 3.60559 23.7738 3.58894 23.7265 3.55676C23.6793 3.52462 23.6428 3.4789 23.622 3.4259L23.5302 3.19153C23.5159 3.1554 23.4943 3.12234 23.4667 3.09485C23.4392 3.06742 23.4063 3.04567 23.37 3.03137L23.1357 2.9386C23.0826 2.9174 23.0368 2.88026 23.0048 2.83313C22.9729 2.78607 22.956 2.73072 22.956 2.67395C22.956 2.61706 22.9728 2.56092 23.0048 2.51379C23.0368 2.46683 23.0828 2.43045 23.1357 2.4093L23.37 2.31653C23.4435 2.28774 23.5008 2.22954 23.5302 2.15637L23.622 1.92297C23.6427 1.87 23.6794 1.82427 23.7265 1.79211C23.7738 1.75994 23.8303 1.74231 23.8876 1.74231Z" fill="url(#paint0_linear_223_1776)"/>
                                    <defs>
                                    <linearGradient id="paint0_linear_223_1776" x1="19.2288" y1="0.500122" x2="19.2288" y2="14.1656" gradientUnits="userSpaceOnUse">
                                        <stop stop-color="#0072E1"/>
                                        <stop offset="1" stop-color="#003E7B"/>
                                    </linearGradient>
                                    </defs>
                                </svg>
                                { translations['ct-ai-create-website'] }
                            </button>
                        </div>
                    {/*@ts-ignore*/}
                    </builderall-loader>
                </div>
                </Show>
            </>
        );
    }
);
